// The warmup's stored sentences, said in Hungarian.
//
// The scheduler writes its reasons in English, from the templates in
// `packages/db/emc_db/warmup/reasons.py` — on `warmup_day.reason`, on
// `ip_pool.paused_reason`, on the pool's state-machine events and on its
// alerts — and they stay English in the database: that is the machine's
// record. This file recognises each template and says it on the page. One
// pattern per Python template, under the same code; anything it does not
// recognise (a person's own words, the watchdog's signal text, an old row)
// is shown as it was written, never guessed at.
//
// tests/admin/test_warmup_reasons_in_hungarian.py runs the real scheduler,
// collects every sentence it writes, and fails if one of them comes out of
// here unrecognised, or if a code on either side has no partner.

import { count } from "@/components/emc/format";
import { statusLook } from "@/components/emc/status";

type Groups = Record<string, string>;

interface Reading {
  code: string;
  pattern: RegExp;
  say: (groups: Groups) => string;
}

const NUMBER = String.raw`-?\d+(?:\.\d+)?`;
const WHO = String.raw`[^\s:@]+@[^\s:]+`;

/** "3.5" → "3,5%": the decimals as the scheduler printed them. */
function rate(value: string | undefined): string {
  return value === undefined ? "—" : `${value.replace(".", ",")}%`;
}

/** "16000" → "16 000". */
function whole(value: string | undefined): string {
  const parsed = Number(value);
  return value !== undefined && Number.isFinite(parsed) ? count(parsed) : (value ?? "—");
}

function rates(groups: Groups): string {
  return `bounce ${rate(groups.bounce)}, panasz ${rate(groups.complaint)}`;
}

function decisionWord(decision: string | undefined): string {
  return statusLook("warmupDecision", decision ?? "").label;
}

// The order is the Python table's, and matters as it does there: the
// particular before the general, the alerts before what they may wrap, the
// person's free note last.
const READINGS: Reading[] = [
  {
    code: "not_automatic",
    pattern: /^pool is (?<state>[a-z_]+); nothing automatic$/s,
    say: (g) => `a pool állapota „${statusLook("warmup", g.state ?? "").label}”: az ütemező nem mozdítja`,
  },
  {
    code: "no_evidence",
    pattern: new RegExp(`^no evidence: (?<sent>${NUMBER}) sent yesterday, (?<min_sample>${NUMBER}) needed to decide$`, "s"),
    say: (g) => `nincs elég adat: tegnap ${whole(g.sent)} levél ment ki, a döntéshez ${whole(g.min_sample)} kell`,
  },
  {
    code: "over_pause",
    pattern: new RegExp(
      `^bounce (?<bounce>${NUMBER}) % or complaint (?<complaint>${NUMBER}) % over the pause threshold$`,
      "s",
    ),
    say: (g) => `${rates(g)} — a megállítási küszöb fölött`,
  },
  {
    code: "at_target",
    pattern: new RegExp(`^at the target of (?<target>${NUMBER}) per IP per day$`, "s"),
    say: (g) => `elérte a célt: ${whole(g.target)} / IP / nap`,
  },
  {
    code: "growth_flat",
    pattern: /^growth factor raises nothing$/s,
    say: () => "a növekedési szorzó nem emel",
  },
  {
    code: "raise",
    pattern: new RegExp(
      `^bounce (?<bounce>${NUMBER}) % and complaint (?<complaint>${NUMBER}) % clear; ` +
        `(?<current>${NUMBER}) -> (?<next>${NUMBER})$`,
      "s",
    ),
    say: (g) => `${rates(g)} — tiszta nap; emelés ${whole(g.current)} → ${whole(g.next)} / IP`,
  },
  {
    code: "between",
    pattern: new RegExp(
      `^bounce (?<bounce>${NUMBER}) % or complaint (?<complaint>${NUMBER}) % between the thresholds; holding$`,
      "s",
    ),
    say: (g) => `${rates(g)} — az emelési és a megállítási küszöb között; a limit marad`,
  },
  // One SES account's number (2026-10-23): `emc_db.warmup.account`.
  {
    code: "at_account_target",
    pattern: new RegExp(`^at the account's target of (?<target>${NUMBER}) a day$`, "s"),
    say: (g) => `elérte a fiók célját: ${whole(g.target)} / nap`,
  },
  {
    code: "owner_number",
    pattern: /^(?<reason>.+); the number is a person's, so the ramp keeps it$/s,
    say: (g) => `${reasonText(g.reason)} — de a keretet ember adta meg, a rámpa nem emeli`,
  },
  {
    code: "pool_holds_account",
    pattern: /^pool (?<pool>[A-Z0-9_]+) is (?<state>[a-z_]+); the account holds$/s,
    say: (g) =>
      `a(z) ${g.pool} pool „${statusLook("warmup", g.state ?? "").label}”, ezért a fiók kerete marad`,
  },
  {
    code: "pool_not_clean",
    pattern: new RegExp(
      `^pool (?<pool>[A-Z0-9_]+): bounce (?<bounce>${NUMBER}) % or complaint (?<complaint>${NUMBER}) % ` +
        `not clear; the account holds$`,
      "s",
    ),
    say: (g) => `a(z) ${g.pool} pool: ${rates(g)} — nem tiszta nap, ezért a fiók kerete marad`,
  },
  {
    code: "pool_throttled",
    pattern: /^pool (?<pool>[A-Z0-9_]+) is slowed by the watchdog; the account holds$/s,
    say: (g) => `az őrszem lassítja a(z) ${g.pool} poolt, ezért a fiók kerete marad`,
  },
  {
    code: "account_paused_today",
    pattern: new RegExp(
      `^account (?<account>[A-Z0-9]+) today: bounce (?<bounce>${NUMBER}) %, ` +
        `complaint (?<complaint>${NUMBER}) % on (?<sent>${NUMBER}) sent$`,
      "s",
    ),
    say: (g) =>
      `a(z) ${g.account} fiók ma ${whole(g.sent)} küldésből ${rates(g)} — a megállítási küszöb fölött`,
  },
  {
    code: "account_stepped_back",
    pattern: new RegExp(
      `^pool (?<pool>[A-Z0-9_]+) stopped; account (?<account>[A-Z0-9]+) steps back to ` +
        `(?<limit>${NUMBER}): (?<reason>.+)$`,
      "s",
    ),
    say: (g) =>
      `a(z) ${g.pool} pool megállt; a(z) ${g.account} fiók kerete ${whole(g.limit)} / napra lép vissza: ` +
      reasonText(g.reason),
  },
  {
    code: "account_started",
    pattern: new RegExp(
      `^account (?<account>[A-Z0-9]+) warmup started at (?<limit>${NUMBER}) a day by (?<who>${WHO})$`,
      "s",
    ),
    say: (g) => `a(z) ${g.account} fiók warmupja elindítva ${whole(g.limit)} / nap kerettel — ${g.who}`,
  },
  {
    code: "account_limit",
    pattern: new RegExp(
      `^account (?<account>[A-Z0-9]+) limit (?<old>${NUMBER}) -> (?<new>${NUMBER}) by (?<who>${WHO}): ` +
        `(?<note>.+)$`,
      "s",
    ),
    say: (g) =>
      `a(z) ${g.account} fiók kézi kerete: ${whole(g.old)} → ${whole(g.new)} / nap — ${g.who}: ${g.note}`,
  },
  {
    code: "account_decided",
    pattern: /^account (?<account>[A-Z0-9]+) (?<decision>[a-z_]+): (?<reason>.+)$/s,
    say: (g) =>
      `a(z) ${g.account} fiók napi döntése, ${decisionWord(g.decision)}: ${reasonText(g.reason)}`,
  },
  {
    code: "paused_today",
    pattern: new RegExp(
      `^today: bounce (?<bounce>${NUMBER}) %, complaint (?<complaint>${NUMBER}) % on (?<sent>${NUMBER}) sent$`,
      "s",
    ),
    say: (g) => `ma ${whole(g.sent)} küldésből ${rates(g)} — a megállítási küszöb fölött`,
  },
  {
    code: "alert_paused_by",
    pattern: new RegExp(`^pool (?<pool>[A-Z0-9_]+) paused by (?<who>${WHO}): (?<reason>.+)$`, "s"),
    say: (g) => `${g.pool} megállítva — ${g.who}: ${reasonText(g.reason)}`,
  },
  {
    code: "alert_paused",
    pattern: /^pool (?<pool>[A-Z0-9_]+) paused: (?<reason>.+)$/s,
    say: (g) => `${g.pool} megállítva: ${reasonText(g.reason)}`,
  },
  {
    code: "alert_started",
    pattern: new RegExp(
      `^pool (?<pool>[A-Z0-9_]+) warmup started at (?<per_ip>${NUMBER}) per IP per day by (?<who>${WHO})$`,
      "s",
    ),
    say: (g) => `${g.pool} warmup elindítva ${whole(g.per_ip)} / IP / nap limittel — ${g.who}`,
  },
  {
    code: "alert_limit",
    pattern: new RegExp(
      `^pool (?<pool>[A-Z0-9_]+) limit set to (?<per_ip>${NUMBER}) per IP by (?<who>${WHO}): (?<note>.+)$`,
      "s",
    ),
    say: (g) => `${g.pool} kézi limit: ${whole(g.per_ip)} / IP — ${g.who}: ${g.note}`,
  },
  {
    code: "alert_resumed",
    pattern: new RegExp(`^pool (?<pool>[A-Z0-9_]+) resumed by (?<who>${WHO}): (?<note>.+)$`, "s"),
    say: (g) => `${g.pool} újra megy — ${g.who}: ${g.note}`,
  },
  {
    code: "alert_decision",
    pattern: /^pool (?<pool>[A-Z0-9_]+) (?<decision>[a-z_]+): (?<reason>.+)$/s,
    say: (g) => `${g.pool} napi döntés, ${decisionWord(g.decision)}: ${reasonText(g.reason)}`,
  },
  // After the alerts, which may wrap it: "pool HU hold: … — de az őrszem …" is
  // the decision alert around a throttled hold.
  {
    code: "throttled",
    pattern: /^(?<reason>.+) — de az őrszem lassítja a poolt, ezért tartunk$/s,
    say: (g) => `${reasonText(g.reason)} — de az őrszem lassítja a poolt, ezért a limit marad`,
  },
  {
    code: "started_note",
    pattern: new RegExp(`^started by (?<who>${WHO}): (?<note>.+)$`, "s"),
    say: (g) => `${g.who} indította: ${g.note}`,
  },
  {
    code: "started",
    pattern: new RegExp(`^started by (?<who>${WHO})$`, "s"),
    say: (g) => `${g.who} indította`,
  },
  {
    code: "manual_limit",
    pattern: new RegExp(`^(?<who>${WHO}): (?<note>.+)$`, "s"),
    say: (g) => `${g.who}: ${g.note}`,
  },
];

/** The codes this file can say — the Python table's, one for one. */
export const REASON_CODES: string[] = READINGS.map((reading) => reading.code);

/** `{ code, text }` for a sentence the warmup wrote, or null for anything else. */
export function readReason(stored: string | null | undefined): { code: string; text: string } | null {
  if (!stored) return null;
  for (const reading of READINGS) {
    const found = reading.pattern.exec(stored);
    if (found !== null) return { code: reading.code, text: reading.say(found.groups ?? {}) };
  }
  return null;
}

/** A stored reason as the page says it: Hungarian when it is one of the
 * warmup's own sentences, otherwise exactly as written. */
export function reasonText(stored: string | null | undefined): string {
  if (!stored) return "";
  return readReason(stored)?.text ?? stored;
}
